import clsx from "clsx";
import React from "react";
import {
  Drawer,
  DrawerProps,
  IconButton,
  Button,
  Box,
  ClickAwayListener,
  Portal,
} from "@mui/material";

import { ReactComponent as CloseIcon } from "../../icons/Close.svg";
import { ReactComponent as BackIcon } from "../../icons/ArrowBack.svg";
import { ReactComponent as RemoveIcon } from "../../icons/Bin.svg";

import {
  WorkoutSection,
  WorkoutExercise,
  ExerciseId,
  DefaultWorkoutExercise,
  WorkoutExerciseMedia,
} from "./types";
import { WorkoutEditSection } from "./WorkoutEditSection";
import {
  createEmptyExercise,
  defaultSectionTitle,
  DEFAULT_VALUE_OF_EXERCISE_REPS,
  normalizeObsoleteExercise,
} from "./utils";
import { WorkoutTitle } from "./WorkoutTitle";
import { WORKOUT_SECTION_HIGHLIGHT_CONTAINER_CLASS } from "../container/HighlightContainer";
import {
  CommonElementId,
  ExerciseTypeExtra,
  Units,
  UnitsTime,
  UserRole,
} from "../../constants";
import { isEqual } from "lodash";
import { updateExerciseLocalStorageConfig } from "../../utils/local-storage";
import { ManageWorkoutExercise } from "./ManageWorkoutExercise";
import {
  ExerciseEditingType,
  ManageLibraryExercise,
} from "../exercise-library/ManageLibraryExercise";

import { ExerciseAssetDto } from "@growth-machine-llc/stridist-api-client";
import { ExerciseDrawerSearch } from "./ExerciseDrawerSearch";
import { ExerciseDrawerData } from "../program-workout-spreadsheet/utils";
import { bindTrigger, usePopupState } from "material-ui-popup-state/hooks";
import { DeleteArchiveConfirmationTooltipButton } from "../program/modals/DeleteConfirmation.tsx/DeleteConfirmationTooltipButton";
import { exerciseDrawerStyles } from "../exercise-library/exerciseDrawerStyles";
import { useCurrentUserRole } from "../../hooks/useCurrentUser";

export enum WorkoutDrawerMode {
  SECTION = "SECTION",
  COMPONENT_EXERCISE = "COMPONENT_EXERCISE",
  LIBRARY_EXERCISE = "LIBRARY_EXERCISE",
  SEARCH_EXERCISE = "SEARCH_EXERCISE",
}

export interface WorkoutDrawerProps extends DrawerProps {
  section?: WorkoutSection;
  exercise?: WorkoutExercise;
  onSelectExercise?: (exercise: WorkoutExercise) => void;
  onUpdate?: (workout: WorkoutSection) => void;
  onCancel?: () => void;
  onRemove?: (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    exercise?: WorkoutExercise,
  ) => void;
  onTitleChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    title: string,
  ) => void;
  drawerMode?: WorkoutDrawerMode;
  setDrawerMode?: (mode: WorkoutDrawerMode) => void;
  readonly?: boolean;
  setSheetsViewDrawerData?: React.Dispatch<
    React.SetStateAction<ExerciseDrawerData>
  >;
}

export function WorkoutDrawer(props: WorkoutDrawerProps) {
  const {
    className,
    section = { exercises: [] } as WorkoutSection,
    exercise: selectedExercise,
    onSelectExercise: setSelectedExercise,
    onTitleChange,
    onUpdate,
    onCancel,
    onRemove,
    drawerMode,
    setDrawerMode,
    readonly,
    setSheetsViewDrawerData,
    ...other
  } = props;
  const { onClose } = props;
  const s = exerciseDrawerStyles();

  const role = useCurrentUserRole();

  const [updateComponentExercise, setUpdateComponentExercise] =
    React.useState(true);

  const [defaultComponentExerciseValue, setDefaultComponentExerciseValue] =
    React.useState<DefaultWorkoutExercise>(null);

  const [defaultLibraryExerciseValue, setDefaultLibraryExerciseValue] =
    React.useState<DefaultWorkoutExercise>(null);

  //TODO_exercise_library: Optimize firstOpenFromSpreadsheet state
  //This state is used to open autocomplete with delay because of the animation
  const [firstOpenFromSpreadsheet, setFirstOpenFromSpreadsheet] =
    React.useState<boolean>(
      !!setSheetsViewDrawerData &&
        (drawerMode === WorkoutDrawerMode.SEARCH_EXERCISE ||
          drawerMode === null),
    );

  React.useEffect(() => {
    {
      if (
        !!setSheetsViewDrawerData &&
        (drawerMode === WorkoutDrawerMode.SEARCH_EXERCISE ||
          drawerMode === null)
      ) {
        setFirstOpenFromSpreadsheet(true);
      }
    }
  }, [props.open]);

  React.useEffect(() => {
    if (
      !updateComponentExercise &&
      drawerMode !== WorkoutDrawerMode.LIBRARY_EXERCISE
    ) {
      setUpdateComponentExercise(true);
    }
  }, [drawerMode, updateComponentExercise]);

  const [libraryExercise, setLibraryExercise] =
    React.useState<ExerciseAssetDto | null>(null);

  const handleUpdate = React.useCallback(
    (workout: WorkoutSection) => {
      if (onUpdate) {
        onUpdate(workout);
      }
    },
    [onUpdate],
  );

  // Reset exercise default values on exercise change
  React.useEffect(() => {
    resetDefaultValues();
  }, [selectedExercise]);

  const handleUpdateExercise = React.useCallback(
    // Add keepDrawerMode parameter to allow keeping the drawer mode after updating the exercise.
    // Useful for editing exercises without closing the drawer.
    (exercise: WorkoutExercise, keepDrawerMode?: boolean) => {
      const exercises = [...section.exercises];
      const index = exercises?.findIndex((it) => it.id === exercise.id);
      if (index !== -1) {
        if (isEqual(exercises[index], exercise) && !keepDrawerMode) {
          onClose(event, "backdropClick");
          return;
        }
        exercises[index] = exercise;
      } else {
        exercises?.push(exercise);
      }

      handleUpdate({
        ...section,
        exercises,
      });

      updateExerciseLocalStorageConfig({
        typeReps: exercise?.typeReps,
        typeSet: exercise?.typeSet,
        units: exercise?.units as Units | UnitsTime,
        ...(exercise?.typeExtraMeasurement !== ExerciseTypeExtra.NONE && {
          typeExtraMeasurement: exercise?.typeExtraMeasurement,
        }),
      });
      if (!keepDrawerMode) {
        setDrawerMode(WorkoutDrawerMode.SECTION);
        setSelectedExercise && setSelectedExercise(null);
        resetDefaultValues();
      }
    },
    [handleUpdate, section, setSelectedExercise],
  );

  const handleUpdateWorkout = React.useCallback(
    (workout: WorkoutSection) => {
      handleUpdate(workout);
    },
    [handleUpdate],
  );

  const handleAddExercise = React.useCallback(() => {
    setSelectedExercise && setSelectedExercise(null);
    setDrawerMode(WorkoutDrawerMode.SEARCH_EXERCISE);
  }, [setSelectedExercise]);

  const handleCancelExercise = React.useCallback(() => {
    setSelectedExercise && setSelectedExercise(null);
    onCancel && onCancel();
  }, [setSelectedExercise]);

  const handleEditExercise = React.useCallback(
    (exerciseId: ExerciseId) => {
      const exercise = section.exercises?.find((it) => it.id === exerciseId);
      setSelectedExercise(normalizeObsoleteExercise(exercise));
      setDrawerMode(WorkoutDrawerMode.COMPONENT_EXERCISE);
    },
    [section.exercises, setSelectedExercise],
  );

  const handleRemoveExercise = React.useCallback(
    (exerciseId: ExerciseId, returnBack?: boolean) => {
      handleUpdate({
        ...section,
        exercises: section.exercises?.filter((it) => it.id !== exerciseId),
      });

      returnBack && handleBack();
    },
    [handleUpdate, section],
  );

  const handleClose = React.useCallback(
    (event) => {
      onClose(event, "backdropClick");
    },
    [onClose],
  );

  const handleLibraryExerciseMode = React.useCallback(() => {
    setSelectedExercise &&
      setSelectedExercise(
        createEmptyExercise(3, DEFAULT_VALUE_OF_EXERCISE_REPS),
      );
    setDrawerMode(WorkoutDrawerMode.LIBRARY_EXERCISE);
    setLibraryExercise(null);
  }, []);

  const handleSectionMode = React.useCallback(() => {
    setDrawerMode(WorkoutDrawerMode.SECTION);
    setSelectedExercise && setSelectedExercise(null);
    setLibraryExercise(null);
  }, []);

  const handleCreateExerciseDialogOpen = React.useCallback(() => {
    role === UserRole.COACH && handleLibraryExerciseMode();
  }, [role]);

  const handleResetComponentExercise = React.useCallback(() => {
    setDrawerMode(WorkoutDrawerMode.SEARCH_EXERCISE);
  }, []);

  const handleSelectExercise = React.useCallback(
    (exercise: WorkoutExercise) => {
      setSelectedExercise && setSelectedExercise(exercise);
      setSheetsViewDrawerData &&
        setSheetsViewDrawerData((prevData) => {
          if (!prevData) return prevData;

          const updatedExercise =
            prevData.exercise?.id === exercise.id
              ? exercise
              : prevData.exercise;

          return {
            ...prevData,
            exercise: updatedExercise,
          };
        });
    },
    [setSheetsViewDrawerData],
  );

  const handleSelectLibraryExercise = React.useCallback(
    (exercise: WorkoutExercise) => {
      if (updateComponentExercise) {
        handleSelectExercise(exercise);
        resetDefaultValues();
      } else {
        setDefaultLibraryExerciseValue(exercise);
      }
      setDrawerMode(WorkoutDrawerMode.COMPONENT_EXERCISE);
    },
    [handleSelectExercise, updateComponentExercise],
  );

  const handleBack = React.useCallback(() => {
    if (drawerMode === WorkoutDrawerMode.LIBRARY_EXERCISE) {
      !updateComponentExercise
        ? setDrawerMode(WorkoutDrawerMode.COMPONENT_EXERCISE)
        : handleResetComponentExercise();
    } else if (
      drawerMode === WorkoutDrawerMode.COMPONENT_EXERCISE ||
      drawerMode === WorkoutDrawerMode.SEARCH_EXERCISE
    ) {
      handleSectionMode();
      resetDefaultValues();
    } else {
      handleClose(null);
    }
  }, [
    drawerMode,
    setDrawerMode,
    handleResetComponentExercise,
    updateComponentExercise,
  ]);

  const handleRemove = React.useCallback(
    (event) => {
      if (selectedExercise) {
        handleBack();
      } else {
        handleClose(event);
      }

      onRemove(event, selectedExercise);
    },
    [handleBack, handleClose, onRemove, selectedExercise],
  );

  const handleClickAway = React.useCallback(
    (event) => {
      const el = event.target as HTMLDivElement;

      if (el && "closest" in el) {
        const closestWorkoutSection = el.closest(
          `.${WORKOUT_SECTION_HIGHLIGHT_CONTAINER_CLASS}`,
        );
        const withinMainRoot = el.closest("#root");
        const withinComponentDialog = el.closest(
          "#" + CommonElementId.COMPONENT_DIALOG,
        );

        if (
          withinMainRoot ||
          (withinComponentDialog && !closestWorkoutSection)
        ) {
          handleClose(event);
        }
      }
    },
    [handleClose],
  );

  const resetDefaultValues = () => {
    setDefaultComponentExerciseValue(null);
    setDefaultLibraryExerciseValue(null);
  };

  const deleteComponentExerciseConfirmation = usePopupState({
    variant: "popover",
    popupId: "delete-component-exercise-confirmation",
  });

  return (
    <Portal>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Drawer
          className={clsx(s.root, className)}
          classes={{ paper: s.paper }}
          anchor="right"
          variant="persistent"
          {...other}
        >
          <Box className={s.buttons}>
            <IconButton
              className={clsx(s.headerButton, s.backButton)}
              onClick={handleBack}
              size="large"
            >
              <BackIcon />
            </IconButton>

            <Box>
              {(drawerMode === WorkoutDrawerMode.COMPONENT_EXERCISE ||
                drawerMode === WorkoutDrawerMode.SECTION) && (
                <>
                  <DeleteArchiveConfirmationTooltipButton
                    popupState={deleteComponentExerciseConfirmation}
                    closeDialog={() => {
                      deleteComponentExerciseConfirmation.close();
                    }}
                    confirmAction={(event) => {
                      if (selectedExercise) {
                        handleRemoveExercise(selectedExercise.id, true);
                      } else {
                        handleRemove(event);
                      }
                      deleteComponentExerciseConfirmation.close();
                    }}
                    anchorOrigin={{ vertical: "center", horizontal: "left" }}
                    transformOrigin={{
                      vertical: "center",
                      horizontal: "right",
                    }}
                    deleteItem={true}
                  />
                  <IconButton
                    {...bindTrigger(deleteComponentExerciseConfirmation)}
                    className={clsx(s.headerButton, s.deleteButton)}
                    size="large"
                  >
                    <RemoveIcon />
                  </IconButton>
                </>
              )}

              <IconButton
                className={clsx(s.headerButton, s.closeButton)}
                onClick={handleClose}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {drawerMode === WorkoutDrawerMode.SECTION && (
            <WorkoutTitle
              classes={{ root: s.title, text: s.titleText }}
              value={section.title}
              onChange={onTitleChange}
              defaultValue={defaultSectionTitle}
              placeholder={defaultSectionTitle}
            />
          )}

          {drawerMode === WorkoutDrawerMode.SEARCH_EXERCISE && (
            <ExerciseDrawerSearch
              sectionName={section?.title}
              workoutExercise={selectedExercise}
              onCreateNewExercise={handleCreateExerciseDialogOpen}
              onExerciseSelect={handleSelectLibraryExercise}
              readonly={readonly}
              firstOpenFromSpreadsheet={firstOpenFromSpreadsheet}
              setFirstOpenFromSpreadsheet={setFirstOpenFromSpreadsheet}
              onSuggestExerciseComplete={handleSelectLibraryExercise}
            />
          )}

          {drawerMode === WorkoutDrawerMode.COMPONENT_EXERCISE && (
            <ManageWorkoutExercise
              key={selectedExercise?.id}
              sectionName={section?.title}
              exercise={selectedExercise}
              onUpdate={(exercise, keepDrawerMode) =>
                handleUpdateExercise(exercise, keepDrawerMode)
              }
              onCancel={handleCancelExercise}
              editLibraryExercise={(exercise, defaultValues) => {
                setLibraryExercise(exercise);
                setUpdateComponentExercise(false);
                setDrawerMode(WorkoutDrawerMode.LIBRARY_EXERCISE);
                setDefaultComponentExerciseValue(defaultValues);
              }}
              defaultLibraryExerciseValues={defaultLibraryExerciseValue}
              defaultComponentExerciseValues={defaultComponentExerciseValue}
              onResetComponentExercise={handleResetComponentExercise}
            />
          )}

          {drawerMode === WorkoutDrawerMode.SECTION && (
            <WorkoutEditSection
              section={section}
              onUpdate={handleUpdateWorkout}
              onEditExercise={handleEditExercise}
              onRemoveExercise={handleRemoveExercise}
            >
              <Button
                variant="outlined"
                className={s.addButton}
                onClick={handleAddExercise}
                fullWidth
              >
                Add exercise
              </Button>
            </WorkoutEditSection>
          )}
          {drawerMode === WorkoutDrawerMode.LIBRARY_EXERCISE && (
            <>
              <ManageLibraryExercise
                key={libraryExercise?.id}
                exerciseAsset={libraryExercise}
                workoutExerciseId={
                  setSheetsViewDrawerData && selectedExercise?.id
                }
                createNewAsset={!libraryExercise}
                onComplete={handleSelectLibraryExercise}
                onSelectExercise={(exercise) => {
                  setLibraryExercise(exercise);
                  handleSelectExercise(normalizeObsoleteExercise(exercise));
                }}
                editingType={ExerciseEditingType.ALL}
                defaultComponentExerciseValues={defaultComponentExerciseValue}
                showHeader={false}
                disableChangeExercise={true}
              />
            </>
          )}
        </Drawer>
      </ClickAwayListener>
    </Portal>
  );
}
